import type { PortalScope } from "@productlane/lib/src/portal-visibility"
import {
	notShownOn,
	portalName,
	requestPortalId,
} from "@productlane/lib/src/portal-visibility"
import { Button } from "@productlane/ui"

import { toast } from "@/components/toast"
import { usePortalEntries } from "@/lib/hooks/use-portal-entries"
import { useVisibilityActions } from "@/lib/hooks/useVisibilityActions"
import { useThreadContext } from "../../thread-context"

interface LinkedItem extends PortalScope {
	type: "issue" | "project"
	id: string
	name: string
}

/**
 * The customer asked on one portal; offer to show what the thread links to
 * there, one click per item.
 */
export function ThreadPortalSuggestions({
	items,
}: {
	items: readonly LinkedItem[]
}) {
	const { thread } = useThreadContext()
	const { entries, namedPortalIds } = usePortalEntries()
	const { canEdit, setPortalVisibility } = useVisibilityActions()

	const portalId = entries.length > 0 ? requestPortalId(thread) : null
	const missing = notShownOn(items, portalId)
	if (!portalId || missing.length === 0 || !canEdit) return null

	const name = portalName(portalId, entries)

	return (
		<div className="mt-1.5 flex flex-col gap-1 rounded-md bg-gray-50 p-2 text-xs dark:bg-gray-850">
			<div className="text-tertiary">Requested on {name}</div>
			{missing.map((item) => (
				<div key={item.id} className="flex items-center gap-2">
					<span className="min-w-0 flex-1 truncate text-primary">
						{item.name}
					</span>
					<Button
						size="sm"
						variant="outline"
						className="flex-none"
						onClick={async () => {
							await setPortalVisibility({
								id: item.id,
								type: item.type,
								portalId,
								value: true,
								namedPortalIds,
								current: item,
							})
							toast.success({
								title: `"${item.name}" is now visible on ${name}`,
							})
						}}
					>
						Show on {name}
					</Button>
				</div>
			))}
		</div>
	)
}
